import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams }  from "react-router-dom";
import {
  User,
  Phone,
  Mail,
  MapPin,
  PawPrint,
  ShieldCheck,
  Heart,
  ClipboardList,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
} from "lucide-react";
import "../static/makeAppointment.css";
import "../static/modals.scss";
import "../static/SignaturePad.css";
import TermsModal from "../components/TermsModal";

const Form = () => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const quickAppointmentId = searchParams.get("quick_id") || "";

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Owner Info
  const [name, setName] = useState("");
  const [phoneMobile, setPhoneMobile] = useState("");
  const [email, setEmail] = useState("smme2027@gmail.com");
  const [postcode, setPostcode] = useState("00000");
  const [address, setAddress] = useState("default");
  const [points, setPoints] = useState("1");
  const [numberOfVisit, setNumberOfVisit] = useState("1");
  const [note, setNote] = useState("No");

  // Pet Info
  const [petName, setPetName] = useState("");
  const [breed, setBreed] = useState("");
  const [catdog, setCatdog] = useState("");
  const [gender, setGender] = useState("");
  const [age, setAge] = useState("0");
  const [behaviour, setBehaviour] = useState("No");
  const [allergies, setAllergies] = useState("No");
  const [vaccinated, setVaccinated] = useState(false);
  const [neutered, setNeutered] = useState(false);
  const [groomingHistory, setGroomingHistory] = useState("");
  const [socialMediaConsent, setSocialMediaConsent] = useState("");

  const getReadableError = (error) => {
    const data = error.response?.data;

    if (!data) return "Submission failed. Please try again.";

    if (typeof data === "string") {
      if (data.includes("DisallowedHost")) return "Domain is not allowed in backend settings.";
      if (data.includes("readonly database")) return "Database permission problem.";
      return data.slice(0, 240);
    }

    try {
      return JSON.stringify(data);
    } catch {
      return "Submission failed. Please check the console.";
    }
  };

  useEffect(() => {
  const savedPetName = searchParams.get("pet_name");
  const savedPhone = searchParams.get("phone");
  const savedType = searchParams.get("catdog");

  if (savedPetName) setPetName(savedPetName);
  if (savedPhone) setPhoneMobile(savedPhone);
  if (savedType) setCatdog(savedType);
}, [searchParams]);


const handleSubmit = async (e) => {
  e.preventDefault();
  setSubmitting(true);
  setSubmitError("");

  try {
    const payload = new FormData();

    payload.append("quick_appointment_id", quickAppointmentId);
    payload.append("name", name || "");
    payload.append("phone_mobile", phoneMobile || "");
    payload.append("email", email || "");
    payload.append("postcode", postcode || "");
    payload.append("address", address || "");
    payload.append("points", points || "0");
    payload.append("numberofvisit", numberOfVisit || "0");
    payload.append("note", note || "");

    payload.append("pet_name", petName || "");
    payload.append("breed", breed || "");
    payload.append("catdog", catdog || "Dog");
    payload.append("gender", gender || "");
    payload.append("age", age || "0");
    payload.append("behaviour", behaviour || "");
    payload.append("allergies", allergies || "");
    payload.append("vaccinated", vaccinated ? "Yes" : "No");
    payload.append("neutered", neutered ? "Yes" : "No");
    payload.append("grooming_history", groomingHistory || "");
    payload.append("social_media_consent", socialMediaConsent || "No");

    const response = await axios.post(
      "/petgrooming/save-booking-profile/",
      payload,
      {
        headers: { "Content-Type": "multipart/form-data" },
      }
    );

    navigate("/signit", {
      state: {
        owner: {
          pet: response.data.pet_id,
          phone: phoneMobile,
          owner: email || "none@gmail.com",
        },
      },
    });
  } catch (error) {
    console.error("Booking profile error:", error.response?.data || error);
    setSubmitError(
      error.response?.data?.error ||
        "Could not save the owner and pet profile."
    );
  } finally {
    setSubmitting(false);
  }
};

  return (
    <main className="appointment-page">
      <section className="appointment-hero">
        <div>
          <span className="appointment-eyebrow">
            <PawPrint size={16} />
            New customer registration
          </span>
          <h1>Owner & Pet Intake Form</h1>
          <p>
            Add a new owner and pet profile before signature, grooming history,
            and future appointments.
          </p>
        </div>

        <div className="appointment-hero-card">
          <div className="hero-card-icon">
            <ClipboardList size={24} />
          </div>
          <div>
            <strong>Next step</strong>
            <span>After submit, customer goes to signature page.</span>
          </div>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="appointment-form" encType="multipart/form-data">
        {submitError && (
          <div className="appointment-alert">
            <AlertCircle size={20} />
            <span>{submitError}</span>
          </div>
        )}

        <section className="form-panel">
          <div className="panel-header">
            <div className="panel-icon owner">
              <User size={22} />
            </div>
            <div>
              <h2>Owner details</h2>
              <p>Basic customer contact information.</p>
            </div>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Name *</span>
              <div className="field-control">
                <User size={18} />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  placeholder="Owner full name"
                />
              </div>
            </label>

            <label className="field">
              <span>Mobile phone *</span>
              <div className="field-control">
                <Phone size={18} />
                <input
                  type="text"
                  value={phoneMobile}
                  onChange={(e) => setPhoneMobile(e.target.value)}
                  required
                  placeholder="07..."
                />
              </div>
            </label>

            <label className="field">
              <span>Email</span>
              <div className="field-control">
                <Mail size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@email.com"
                />
              </div>
            </label>

            <label className="field">
              <span>Postcode</span>
              <div className="field-control">
                <MapPin size={18} />
                <input
                  type="text"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value)}
                  placeholder="Postcode"
                />
              </div>
            </label>

            <label className="field field-full">
              <span>Address</span>
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows="3"
                placeholder="Customer address"
              />
            </label>

            <label className="field">
              <span>Points</span>
              <input
                type="hidden"
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="0"
              />
            </label>

            <label className="field">
              <span>Number of visits</span>
              <input
                type="hidden"
                value={numberOfVisit}
                onChange={(e) => setNumberOfVisit(e.target.value)}
                placeholder="0"
              />
            </label>

            <label className="field field-full">
              <span>Owner note</span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows="3"
                placeholder="Anything important about the owner..."
              />
            </label>
          </div>
        </section>

        <section className="form-panel">
          <div className="panel-header">
            <div className="panel-icon pet">
              <PawPrint size={22} />
            </div>
            <div>
              <h2>Pet details</h2>
              <p>Everything needed to start a clean pet profile.</p>
            </div>
          </div>

          <div className="form-grid">
            <label className="field">
              <span>Pet name *</span>
              <div className="field-control">
                <PawPrint size={18} />
                <input
                  type="text"
                  value={petName}
                  onChange={(e) => setPetName(e.target.value)}
                  required
                  placeholder="Pet name"
                />
              </div>
            </label>

            <label className="field">
              <span>Breed *</span>
              <input
                type="text"
                value={breed}
                onChange={(e) => setBreed(e.target.value)}
                required
                placeholder="Breed"
              />
            </label>

            <label className="field">
              <span>Type</span>
              <select value={catdog} onChange={(e) => setCatdog(e.target.value)}>
                <option value="">Select type</option>
                <option value="Dog">Dog</option>
                <option value="Cat">Cat</option>
              </select>
            </label>

            <label className="field">
              <span>Gender</span>
              <select value={gender} onChange={(e) => setGender(e.target.value)}>
                <option value="">Select gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </label>

            <label className="field">
              <span>Age</span>
              <input
                type="number"
                value={age}
                onChange={(e) => setAge(e.target.value)}
                placeholder="Age"
              />
            </label>

            <label className="field">
              <span>Behaviour</span>
              <select value={behaviour} onChange={(e) => setBehaviour(e.target.value)}>
                <option value="">Select behaviour</option>
                <option value="🆕 New">🆕 New</option>
                <option value="😠 Aggressive">😠 Aggressive</option>
                <option value="😊 Gentle">😊 Gentle</option>
                <option value="😳 Shy">😳 Shy</option>
                <option value="🤪 Excited">🤪 Excited</option>
                <option value="😴 Tired">😴 Tired</option>
                <option value="😌 Calm">😌 Calm</option>
                <option value="😨 Fearful">😨 Fearful</option>
                <option value="Naughty behavior">Naughty behavior</option>
              </select>
            </label>

            <label className="field field-full">
              <span>Allergies</span>
              <textarea
                value={allergies}
                onChange={(e) => setAllergies(e.target.value)}
                rows="3"
                placeholder="Allergies, medical notes, sensitivities..."
              />
            </label>

            <div className="toggle-row">
              <label className="pretty-check">
                <input
                  type="checkbox"
                  checked={vaccinated}
                  onChange={(e) => setVaccinated(e.target.checked)}
                />
                <span>
                  <ShieldCheck size={18} />
                  Vaccinated
                </span>
              </label>

              <label className="pretty-check">
                <input
                  type="checkbox"
                  checked={neutered}
                  onChange={(e) => setNeutered(e.target.checked)}
                />
                <span>
                  <Heart size={18} />
                  Neutered
                </span>
              </label>
            </div>

            <label className="field field-full">
              <span>Owner request / grooming notes</span>
              <textarea
                value={groomingHistory}
                onChange={(e) => setGroomingHistory(e.target.value)}
                rows="4"
                placeholder="What does the owner want? Style, cut, warnings, previous groom notes..."
              />
            </label>

            <label className="field">
              <span>Social media consent</span>
              <select
                value={socialMediaConsent}
                onChange={(e) => setSocialMediaConsent(e.target.value)}
              >
                <option value="">Select</option>
                <option value="Yes">Yes, allowed</option>
                <option value="No">No</option>
              </select>
            </label>
          </div>
        </section>

        <section className="terms-card">
          <label className="terms-check">
            <input type="checkbox" required />
            <span>
              I agree to the vaccination terms and confirm the information is correct.
            </span>
          </label>
          <TermsModal />
        </section>

        <div className="form-actions">
          <button type="button" className="secondary-button" onClick={() => navigate("/")}>
            Cancel
          </button>

          <button type="submit" className="primary-button" disabled={submitting}>
            {submitting ? (
              "Saving..."
            ) : (
              <>
                Save owner & pet
                <ArrowRight size={18} />
              </>
            )}
          </button>
        </div>
      </form>
    </main>
  );
};

export default Form;